(function() {
  'use strict';

  const MODAL_ID = 'quick-view-modal';
  const PRODUCT_LINK_SELECTOR = 'a.product-card__link, product-card-link a';

  let modal, closeBtn, contentEl, cartCountEl;
  let lightbox, lightboxImg, lightboxClose;
  let previousFocus = null;
  let originalUrl = null;
  let currentSlide = 0;
  let totalSlides = 0;

  // Product navigation state
  let productList = [];
  let currentProductIndex = -1;

  // Variants that hit their max quantity (Shopify returned 422 on add).
  // Once a variant is here, its + button stays disabled for the session.
  const maxedOutVariants = new Set();

  function init() {
    modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    closeBtn = modal.querySelector('.quick-view-modal__close');
    contentEl = modal.querySelector('.quick-view-modal__content');
    cartCountEl = modal.querySelector('[data-cart-count]');
    lightbox = document.getElementById('qv-lightbox');
    lightboxImg = lightbox ? lightbox.querySelector('.qv-lightbox__img') : null;
    lightboxClose = lightbox ? lightbox.querySelector('.qv-lightbox__close') : null;

    setupLightbox();

    document.addEventListener('click', handleProductClick, true);
    closeBtn.addEventListener('click', closeModal);
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('popstate', handlePopState);
    setupTouchGestures();
    setupWheelGesture();
    syncCartCount();

    // Listen for external cart updates (e.g., when cart-overlay removes an item)
    document.addEventListener('cart:updated', handleExternalCartUpdate);

    // Auto-open modal if user lands directly on a product page.
    // We redirect to homepage and open the modal so closing returns to the grid.
    if (window.location.pathname.includes('/products/')) {
      const productPath = window.location.pathname + window.location.search;

      // Replace the URL with homepage (no extra history entry — no "back" loop)
      history.replaceState({}, '', '/');

      // Hide page content immediately while homepage HTML loads
      document.body.classList.add('quick-view-open');

      // Load the homepage HTML and swap it in BEFORE opening the modal,
      // so closing the modal reveals the grid (not the product template)
      loadHomepageContent().then(() => {
        openModal(productPath, null);
      });
    }
  }

  /**
   * Called when something else in the page modifies the cart
   * (e.g., cart-overlay removing an item).
   */
  async function handleExternalCartUpdate(e) {
    if (e.detail && e.detail.cart) {
      if (cartCountEl) updateCartCount(e.detail.cart.item_count || 0);
    } else {
      syncCartCount();
    }

    // Re-check the current modal's + button — if the variant was removed from cart
    // elsewhere (cart overlay), the + should become enabled again
    const btn = contentEl.querySelector('.qv-add');
    if (btn && btn.dataset.variantId) {
      const maxQty = btn.dataset.inventoryQuantity
        ? Number(btn.dataset.inventoryQuantity)
        : null;
      const atMax = await isVariantAtMaxInCart(btn.dataset.variantId, maxQty);
      if (atMax) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('data-in-cart', 'true');
      } else {
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('data-in-cart');
      }
    }
  }

  /**
   * Fetch homepage HTML and replace #MainContent so closing the modal
   * reveals the homepage grid (not the product page template).
   */
  async function loadHomepageContent() {
    try {
      const res = await fetch('/', { headers: { 'Accept': 'text/html' } });
      if (!res.ok) return;
      const html = await res.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const currentMain = document.getElementById('MainContent');
      const newMain = doc.getElementById('MainContent');
      if (currentMain && newMain) {
        currentMain.replaceWith(newMain);
      }
    } catch (err) {
      console.error('Failed to load homepage content:', err);
    }
  }

  function buildProductList() {
    const links = document.querySelectorAll(PRODUCT_LINK_SELECTOR);
    productList = [];
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href && href.includes('/products/') && !productList.includes(href)) {
        productList.push(href);
      }
    });
  }

  function handleProductClick(e) {
    const link = e.target.closest(PRODUCT_LINK_SELECTOR);
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || !href.includes('/products/')) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

    e.preventDefault();
    e.stopPropagation();

    // Skip sold paintings — check BEFORE opening
    const card = link.closest('product-card, .product-card, product-card-link');
    if (card && card.getAttribute('data-sold') === 'true') {
      return;
    }

    buildProductList();
    currentProductIndex = productList.indexOf(href);

    const cardImg = card ? card.querySelector('img') : null;
    const instantImageSrc = cardImg ? (cardImg.currentSrc || cardImg.src) : null;

    openModal(href, instantImageSrc, cardImg);
  }

  async function openModal(productUrl, instantImageSrc, sourceImgEl) {
    if (!modal) return;

    previousFocus = document.activeElement;
    originalUrl = window.location.href;

    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('quick-view-open');

    contentEl.innerHTML = renderSkeleton(instantImageSrc);
    modal.setAttribute('data-loaded', 'true');
    modal.removeAttribute('data-loading');

    history.pushState({ quickView: true, originalUrl }, '', productUrl);

    // === ZOOM-UP animation from card to modal ===
    if (sourceImgEl) {
      requestAnimationFrame(() => {
        const modalImg = contentEl.querySelector('.qv-carousel__slide img');
        if (!modalImg) return;

        const sourceRect = sourceImgEl.getBoundingClientRect();
        const targetRect = modalImg.getBoundingClientRect();

        const deltaX = sourceRect.left - targetRect.left + (sourceRect.width - targetRect.width) / 2;
        const deltaY = sourceRect.top - targetRect.top + (sourceRect.height - targetRect.height) / 2;
        const scaleX = sourceRect.width / targetRect.width;
        const scaleY = sourceRect.height / targetRect.height;

        modalImg.style.transformOrigin = 'center center';
        modalImg.style.transition = 'none';
        modalImg.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`;

        void modalImg.offsetWidth;

        modalImg.style.transition = 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)';
        modalImg.style.transform = 'translate(0, 0) scale(1, 1)';

        setTimeout(() => {
          modalImg.style.transition = '';
          modalImg.style.transform = '';
          modalImg.style.transformOrigin = '';
        }, 500);
      });
    }

    try {
      const productData = await fetchProductData(productUrl);
      contentEl.innerHTML = renderProduct(productData);
      bindCarousel();
      bindAddToCart(productData);
      closeBtn.focus();
    } catch (err) {
      console.error('Quick view failed, falling back to navigation:', err);
      window.location.href = productUrl;
    }
  }

  let isNavigating = false;
  async function navigateToProduct(direction) {
    if (productList.length === 0 || currentProductIndex === -1) return;
    if (isNavigating) return;
    isNavigating = true;

    try {
      let newIndex = currentProductIndex + direction;
      if (newIndex < 0) newIndex = productList.length - 1;
      if (newIndex >= productList.length) newIndex = 0;

      currentProductIndex = newIndex;
      const nextUrl = productList[newIndex];

      const nextLink = document.querySelector(`a[href="${nextUrl}"]`);
      const nextCard = nextLink ? nextLink.closest('product-card, .product-card, product-card-link') : null;
      const nextImg = nextCard ? nextCard.querySelector('img') : null;
      const instantImageSrc = nextImg ? (nextImg.currentSrc || nextImg.src) : null;

      const nextTitleEl = nextCard ? nextCard.querySelector('[class*="title"], h3, h4') : null;
      const instantTitle = nextTitleEl ? nextTitleEl.textContent.trim() : '';

      const track = contentEl.querySelector('.qv-carousel__track');
      const dotsEl = contentEl.querySelector('.qv-carousel__dots');
      if (!track) return;

      const dataPromise = fetchProductData(nextUrl).catch(err => {
        console.error('Failed to fetch product data:', err);
        return null;
      });

      if (instantTitle) {
        const titleEl = contentEl.querySelector('.qv-title');
        if (titleEl) titleEl.textContent = instantTitle;
      }
      const priceEl = contentEl.querySelector('.qv-price');
      if (priceEl) priceEl.style.opacity = '0.3';

      if (dotsEl) {
        dotsEl.style.opacity = '0';
        dotsEl.style.transition = 'opacity 0.2s ease';
      }

      const exitTransform = direction > 0 ? 'translateY(-100%)' : 'translateY(100%)';
      const enterStartTransform = direction > 0 ? 'translateY(100%)' : 'translateY(-100%)';

      track.classList.add('qv-carousel__track--sliding');
      track.style.transform = exitTransform;
      track.style.opacity = '0';

      history.replaceState({ quickView: true, originalUrl }, '', nextUrl);

      await new Promise(resolve => setTimeout(resolve, 350));

      if (instantImageSrc) {
        track.innerHTML = `
          <div class="qv-carousel__slide" data-active="true">
            <img src="${instantImageSrc}" alt="" />
          </div>
        `;
      }

      track.style.transition = 'none';
      track.style.transform = enterStartTransform;
      void track.offsetHeight;

      track.style.transition = '';
      track.style.transform = 'translateY(0)';
      track.style.opacity = '1';

      await new Promise(resolve => setTimeout(resolve, 350));
      track.classList.remove('qv-carousel__track--sliding');
      track.style.transform = '';
      track.style.opacity = '';

      const productData = await dataPromise;
      if (productData) {
        updateProductInPlace(productData);
      }

      if (priceEl) priceEl.style.opacity = '';
    } catch (err) {
      console.error('navigateToProduct error:', err);
    } finally {
      isNavigating = false;
    }
  }

  function updateProductInPlace(data) {
    const titleEl = contentEl.querySelector('.qv-title');
    if (titleEl && titleEl.textContent.trim() !== data.title.trim()) {
      titleEl.textContent = data.title;
    }

    const priceEl = contentEl.querySelector('.qv-price');
    if (priceEl) priceEl.textContent = data.price;

    const carouselEl = contentEl.querySelector('.qv-carousel');
    const showArrows = data.images.length > 1;

    // Arrows: create if missing, remove if not needed
    let prevArrow = contentEl.querySelector('.qv-carousel__arrow--prev');
    let nextArrow = contentEl.querySelector('.qv-carousel__arrow--next');

    if (showArrows && carouselEl) {
      if (!prevArrow) {
        prevArrow = document.createElement('button');
        prevArrow.className = 'qv-carousel__arrow qv-carousel__arrow--prev';
        prevArrow.setAttribute('aria-label', 'Previous image');
        prevArrow.type = 'button';
        prevArrow.innerHTML = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>';
        carouselEl.insertBefore(prevArrow, carouselEl.firstChild);
      }
      if (!nextArrow) {
        nextArrow = document.createElement('button');
        nextArrow.className = 'qv-carousel__arrow qv-carousel__arrow--next';
        nextArrow.setAttribute('aria-label', 'Next image');
        nextArrow.type = 'button';
        nextArrow.innerHTML = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>';
        carouselEl.appendChild(nextArrow);
      }
      prevArrow.style.display = '';
      nextArrow.style.display = '';
    } else {
      if (prevArrow) prevArrow.remove();
      if (nextArrow) nextArrow.remove();
    }

    // Track: preserve the first slide if its image is already loaded,
    // only append the additional slides. This prevents the flicker.
    const track = contentEl.querySelector('.qv-carousel__track');
    if (track) {
      const existingSlides = track.querySelectorAll('.qv-carousel__slide');
      const existingFirstImg = existingSlides[0] ? existingSlides[0].querySelector('img') : null;

      if (existingFirstImg && data.images[0]) {
        // Keep the first slide, just upgrade its src silently if needed
        const currentSrc = existingFirstImg.src;
        const targetSrc = data.images[0];

        // Remove any extra slides beyond the first
        for (let i = existingSlides.length - 1; i >= 1; i--) {
          existingSlides[i].remove();
        }

        // Make sure first slide has correct attributes
        existingSlides[0].setAttribute('data-index', '0');
        existingSlides[0].setAttribute('data-active', 'true');

        // Only swap the src if it actually differs (and preload to avoid flicker)
        if (currentSrc !== targetSrc) {
          const preload = new Image();
          preload.onload = () => { existingFirstImg.src = targetSrc; };
          preload.src = targetSrc;
        }

        // Append the rest of the images as new slides
        data.images.slice(1).forEach((src, idx) => {
          const slide = document.createElement('div');
          slide.className = 'qv-carousel__slide';
          slide.setAttribute('data-index', String(idx + 1));
          slide.innerHTML = `<img src="${src}" alt="" loading="lazy" />`;
          track.appendChild(slide);
        });
      } else {
        // Cold path — no existing first slide, do full render
        track.innerHTML = data.images.map((src, i) => `
          <div class="qv-carousel__slide" data-index="${i}" ${i === 0 ? 'data-active="true"' : ''}>
            <img src="${src}" alt="" loading="${i === 0 ? 'eager' : 'lazy'}" />
          </div>
        `).join('');
      }
    }

    // Dots: rebuild and wipe stuck inline styles
    let dotsEl = contentEl.querySelector('.qv-carousel__dots');
    if (showArrows) {
      const dotsHtml = data.images.map((_, i) => `
        <button class="qv-carousel__dot" data-index="${i}" ${i === 0 ? 'data-active="true"' : ''} aria-label="Image ${i + 1}" type="button"></button>
      `).join('');

      if (dotsEl) {
        dotsEl.style.cssText = '';
        dotsEl.innerHTML = dotsHtml;
      } else if (carouselEl) {
        dotsEl = document.createElement('div');
        dotsEl.className = 'qv-carousel__dots';
        dotsEl.innerHTML = dotsHtml;
        carouselEl.insertAdjacentElement('afterend', dotsEl);
      }
    } else if (dotsEl) {
      dotsEl.remove();
    }

    const addBtn = contentEl.querySelector('.qv-add');
    if (addBtn) {
      addBtn.dataset.variantId = data.variantId || '';
      addBtn.dataset.inventoryQuantity = (data.inventoryQuantity !== null && data.inventoryQuantity !== undefined) ? data.inventoryQuantity : '';

      // CRITICAL: Always clear the previous product's disabled state first.
      // Without this, when scrolling from a sold-out item to an available one,
      // the button stays disabled for ~1s until bindAddToCart's async check finishes.
      addBtn.removeAttribute('data-in-cart');
      addBtn.removeAttribute('data-added');
      addBtn.removeAttribute('data-loading');

      if (data.available) {
        addBtn.removeAttribute('disabled');
        addBtn.removeAttribute('aria-disabled');
      } else {
        addBtn.setAttribute('disabled', '');
        addBtn.setAttribute('aria-disabled', 'true');
      }
    }

    const productEl = contentEl.querySelector('.qv-product');
    const oldDesc = contentEl.querySelector('.qv-description');

    const hasDescription = data.description && data.description.trim().length > 0;

    if (hasDescription) {
      if (oldDesc) {
        oldDesc.innerHTML = data.description;
      } else if (productEl) {
        const newDesc = document.createElement('div');
        newDesc.className = 'qv-description';
        newDesc.innerHTML = data.description;
        productEl.appendChild(newDesc);
      }
    } else {
      if (oldDesc) oldDesc.remove();
    }

    // Always show description (no toggle anymore)
    if (productEl) productEl.setAttribute('data-info-open', 'true');


    bindCarousel();
    bindAddToCart(data);
  }


  async function fetchProductData(productUrl) {
    // Strip query string and add .js to get the JSON endpoint
    const cleanUrl = productUrl.split('?')[0].replace(/\/$/, '');
    const jsonUrl = cleanUrl + '.js';

    const response = await fetch(jsonUrl, {
      headers: { 'Accept': 'application/json' }
    });

    if (!response.ok) throw new Error('Fetch failed: ' + response.status);

    const product = await response.json();

    // Pick images — upgrade them to a sensible resolution
    const images = (product.images || []).map(src => {
      // Shopify image URLs allow size suffixes like _1200x.jpg
      return src.replace(/(\.[a-z]+)(\?.*)?$/i, '_1200x$1$2');
    });

    // Format price using shop currency
    const formattedPrice = formatMoney(product.price);

    // Option names for this product, e.g. ["Size"] or ["Size","Color"] or ["Frame"]
    const optionNames = (product.options || []).map(o =>
      typeof o === 'string' ? o : o.name
    );

    // Full variant list with their option values
    const variants = (product.variants || []).map(v => ({
      id: v.id,
      title: v.title,
      available: !!v.available,
      // option values: ["M","Black"] etc. Shopify provides option1/2/3
      options: [v.option1, v.option2, v.option3].filter(o => o !== null && o !== undefined),
      inventoryQuantity: v.inventory_quantity !== undefined ? v.inventory_quantity : null
    }));

    // A product has "real" options if it's not the single default variant
    const isDefaultOnly =
      variants.length === 1 &&
      variants[0].title === 'Default Title';
    const hasRealVariants = !isDefaultOnly && variants.length > 0;

    // For each option (by index), the list of unique values
    const optionValues = optionNames.map((name, idx) => {
      const seen = [];
      variants.forEach(v => {
        const val = v.options[idx];
        if (val !== undefined && !seen.includes(val)) seen.push(val);
      });
      return { name: name, index: idx, values: seen };
    });

    const firstAvailable = variants.find(v => v.available);
    const defaultVariant = firstAvailable || variants[0];

    return {
      title: product.title || '',
      price: formattedPrice,
      description: product.description || '',
      images: images,
      variants: variants,
      optionNames: optionNames,
      optionValues: optionValues,
      hasRealVariants: hasRealVariants,
      // Single-variant product → pre-select it. Multi → null until user picks.
      variantId: hasRealVariants ? null : (defaultVariant ? defaultVariant.id : null),
      available: !!firstAvailable,
      inventoryQuantity: (!hasRealVariants && defaultVariant) ? defaultVariant.inventoryQuantity : null
    };
  }

  /**
   * Format a price (in cents) into the shop's display currency.
   * Uses window.Shopify.currency if available, otherwise defaults to USD.
   */
  function formatMoney(cents) {
    const amount = (cents / 100);
    const currencyCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode
      }).format(amount);
    } catch (e) {
      // Fallback if currency code is bogus
      return '$' + amount.toFixed(2);
    }
  }

  function renderSkeleton(instantImageSrc) {
    return `
      <div class="qv-product qv-product--skeleton">
        <div class="qv-carousel">
          <div class="qv-carousel__track">
            ${instantImageSrc ? `
              <div class="qv-carousel__slide" data-active="true">
                <img src="${instantImageSrc}" alt="" />
              </div>
            ` : ''}
          </div>
        </div>
        <div class="qv-skeleton-line qv-skeleton-line--title"></div>
        <div class="qv-skeleton-line qv-skeleton-line--price"></div>
      </div>
    `;
  }

  function renderProduct(data) {
    const slidesHtml = data.images.map((src, i) => `
      <div class="qv-carousel__slide" data-index="${i}" ${i === 0 ? 'data-active="true"' : ''}>
        <img src="${src}" alt="" loading="${i === 0 ? 'eager' : 'lazy'}" />
      </div>
    `).join('');

    const dotsHtml = data.images.map((_, i) => `
      <button class="qv-carousel__dot" data-index="${i}" ${i === 0 ? 'data-active="true"' : ''} aria-label="Image ${i + 1}"></button>
    `).join('');

    const showArrows = data.images.length > 1;
    const hasDescription = data.description && data.description.trim().length > 0;

    return `
      <div class="qv-product" data-info-open="true">
        <div class="qv-carousel">
          ${showArrows ? `
            <button class="qv-carousel__arrow qv-carousel__arrow--prev" aria-label="Previous image">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
          ` : ''}
          <div class="qv-carousel__track">
            ${slidesHtml}
          </div>
          ${showArrows ? `
            <button class="qv-carousel__arrow qv-carousel__arrow--next" aria-label="Next image">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          ` : ''}
        </div>
        ${showArrows ? `<div class="qv-carousel__dots">${dotsHtml}</div>` : ''}

        <div class="qv-title-row">
          <h1 class="qv-title">${escapeHtml(data.title)}</h1>
        </div>

        <div class="qv-price">${data.price}</div>


        ${data.hasRealVariants ? `
          <div class="qv-options">
            ${data.optionValues.map(opt => `
              <div class="qv-option" data-option-index="${opt.index}">
                <div class="qv-option__name">${escapeHtml(opt.name)}</div>
                <div class="qv-option__values">
                  ${opt.values.map(val => `
                    <button class="qv-option__btn"
                            type="button"
                            data-option-index="${opt.index}"
                            data-option-value="${escapeHtml(val)}">
                      ${escapeHtml(val)}
                    </button>
                  `).join('')}
                </div>
              </div>
            `).join('')}
          </div>
        ` : ''}

        <div class="qv-actions">
          <button class="qv-add"
                  data-variant-id="${data.variantId || ''}"
                  ${data.hasRealVariants ? 'disabled aria-disabled="true" data-needs-variant="true"' : (data.available ? '' : 'disabled aria-disabled="true"')}>
            <span aria-hidden="true">+</span>
            <span class="visually-hidden">Add to cart</span>
          </button>
        </div>

        ${hasDescription ? `
          <div class="qv-description">${data.description}</div>
        ` : ''}
      </div>
    `;
  }
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function bindCarousel() {
    // Clone-and-replace arrows + dots to strip any old event listeners
    const oldPrev = contentEl.querySelector('.qv-carousel__arrow--prev');
    const oldNext = contentEl.querySelector('.qv-carousel__arrow--next');
    if (oldPrev) oldPrev.replaceWith(oldPrev.cloneNode(true));
    if (oldNext) oldNext.replaceWith(oldNext.cloneNode(true));

    const oldDots = contentEl.querySelectorAll('.qv-carousel__dot');
    oldDots.forEach(d => d.replaceWith(d.cloneNode(true)));

    const slides = contentEl.querySelectorAll('.qv-carousel__slide');
    const dots = contentEl.querySelectorAll('.qv-carousel__dot');
    const prev = contentEl.querySelector('.qv-carousel__arrow--prev');
    const next = contentEl.querySelector('.qv-carousel__arrow--next');

    totalSlides = slides.length;
    currentSlide = 0;

    if (totalSlides > 1) {
      const goTo = (index) => {
        const liveSlides = contentEl.querySelectorAll('.qv-carousel__slide');
        const liveDots = contentEl.querySelectorAll('.qv-carousel__dot');
        currentSlide = (index + liveSlides.length) % liveSlides.length;
        liveSlides.forEach((s, i) => i === currentSlide ? s.setAttribute('data-active', 'true') : s.removeAttribute('data-active'));
        liveDots.forEach((d, i) => i === currentSlide ? d.setAttribute('data-active', 'true') : d.removeAttribute('data-active'));
      };

      if (prev) prev.addEventListener('click', (e) => { e.stopPropagation(); goTo(currentSlide - 1); });
      if (next) next.addEventListener('click', (e) => { e.stopPropagation(); goTo(currentSlide + 1); });
      dots.forEach((dot, i) => dot.addEventListener('click', (e) => { e.stopPropagation(); goTo(i); }));
    }
  }

  function bindAddToCart(data) {
  let btn = contentEl.querySelector('.qv-add');
  if (btn) {
    const fresh = btn.cloneNode(true);
    btn.replaceWith(fresh);
    btn = fresh;
  }

  const titleEl = contentEl.querySelector('.qv-title');
  if (!btn) return;

  // === Generic multi-option variant selection ===
  if (data.hasRealVariants) {
    // Tracks the user's current choice per option index, e.g. { 0: "M", 1: "Black" }
    const selection = {};

    const optionBtns = contentEl.querySelectorAll('.qv-option__btn');

    // Find the variant that matches ALL currently selected options.
    function findMatchingVariant() {
      if (Object.keys(selection).length !== data.optionNames.length) return null;
      return data.variants.find(v =>
        data.optionNames.every((_, idx) => v.options[idx] === selection[idx])
      ) || null;
    }

    // Grey out option buttons that can't lead to an available variant.
    function refreshAvailability() {
      optionBtns.forEach(b => {
        const idx = Number(b.dataset.optionIndex);
        const val = b.dataset.optionValue;

        // Hypothetical selection if this button were chosen
        const hypothetical = Object.assign({}, selection);
        hypothetical[idx] = val;

        // Is there any AVAILABLE variant matching all chosen-so-far options?
        const possible = data.variants.some(v => {
          if (!v.available) return false;
          return Object.keys(hypothetical).every(k => v.options[k] === hypothetical[k]);
        });

        if (possible) {
          b.removeAttribute('disabled');
        } else {
          b.setAttribute('disabled', '');
        }
      });
    }

    // Enable/disable the + button based on whether a full valid variant is chosen.
    function updateAddButton() {
      const variant = findMatchingVariant();
      if (variant && variant.available) {
        btn.dataset.variantId = variant.id;
        data.variantId = variant.id;
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('data-needs-variant');

        // If this variant is already in the cart, keep + disabled
        isVariantAtMaxInCart(variant.id, null).then(atMax => {
          // Only apply if this is still the selected variant
          if (btn.dataset.variantId === String(variant.id) && atMax) {
            btn.disabled = true;
            btn.setAttribute('aria-disabled', 'true');
            btn.setAttribute('data-in-cart', 'true');
          }
        });
      } else {
        btn.dataset.variantId = '';
        data.variantId = null;
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('data-needs-variant', 'true');
      }
    }

    optionBtns.forEach(b => {
      b.addEventListener('click', () => {
        if (b.hasAttribute('disabled')) return;

        const idx = Number(b.dataset.optionIndex);
        const val = b.dataset.optionValue;

        selection[idx] = val;

        // Highlight: clear other buttons in the SAME option row, mark this one
        optionBtns.forEach(other => {
          if (Number(other.dataset.optionIndex) === idx) {
            other.removeAttribute('data-selected');
          }
        });
        b.setAttribute('data-selected', 'true');
        btn.removeAttribute('data-in-cart');

        refreshAvailability();
        updateAddButton();
      });
    });

    // Initial pass — grey out fully-unavailable options
    refreshAvailability();
  } else {
    // Single-variant product (painting) — bail if not addable
    if (!data.variantId || !data.available) return;
  }

  const maxQty = null;

  // For single-variant products (paintings), check on open if already in cart.
  // Multi-variant products are checked in updateAddButton() when a variant is picked.
  if (!data.hasRealVariants && data.variantId) {
    isVariantAtMaxInCart(data.variantId, maxQty).then(atMax => {
      if (atMax) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('data-in-cart', 'true');
      }
    });
  }

  const originalTitle = titleEl ? titleEl.textContent : '';

  btn.addEventListener('click', async () => {
    // Merch: if no size selected yet, do nothing
    if (data.hasRealVariants && !btn.dataset.variantId) {
      return;
    }

    btn.disabled = true;
    btn.setAttribute('data-loading', 'true');
    if (titleEl) {
      titleEl.setAttribute('data-state', 'adding');
      titleEl.textContent = 'ADDING';
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id: data.variantId, quantity: 1 }],
          sections: ['cart-icon-bubble', 'cart-drawer']
        })
      });

      const responseBody = await response.json().catch(() => null);

      if (!response.ok) {
        console.error('Add to cart failed:', { status: response.status, body: responseBody });

        if (response.status === 422) {
          maxedOutVariants.add(String(data.variantId));
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('data-in-cart', 'true');
          btn.removeAttribute('data-loading');
          btn.removeAttribute('data-added');
          if (titleEl) {
            titleEl.removeAttribute('data-state');
            titleEl.textContent = originalTitle;
          }
          return;
        }

        alert((responseBody && responseBody.description) || 'Max qty reached.');
        resetButton();
        return;
      }

      if (titleEl) {
        titleEl.setAttribute('data-state', 'added');
        titleEl.textContent = 'ADDED';
      }
      btn.setAttribute('data-added', 'true');

      await notifyCartUpdated(responseBody.sections || null);

      setTimeout(async () => {
        if (titleEl) {
          titleEl.removeAttribute('data-state');
          titleEl.textContent = originalTitle;
        }
        btn.removeAttribute('data-loading');
        btn.removeAttribute('data-added');

        const atMax = await isVariantAtMaxInCart(data.variantId, maxQty);
        if (atMax) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('data-in-cart', 'true');
        } else {
          btn.disabled = false;
        }
      }, 1200);
    } catch (err) {
      console.error('Add to cart error:', err);
      alert('Could not add to cart. Please try again.');
      resetButton();
    }

    function resetButton() {
      if (titleEl) {
        titleEl.removeAttribute('data-state');
        titleEl.textContent = originalTitle;
      }
      btn.removeAttribute('data-loading');
      btn.removeAttribute('data-added');
      btn.disabled = false;
    }
  });
}

  /**
   * Tell the theme the cart has been updated so it can refresh its UI
   * (cart counter, mini-cart drawer, etc.). Horizon and most modern Shopify
   * themes listen for either a custom event or the cart sections API.
   */
  async function notifyCartUpdated() {
    let cart = null;
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (res.ok) cart = await res.json();
    } catch (e) {
      // non-fatal
    }

    // Update the modal's own cart counter
    if (cart && cartCountEl) {
      updateCartCount(cart.item_count);
    }

    // Update Horizon's native cart counter in the header
    if (cart) {
      updateHeaderCartCount(cart.item_count);
    }

    // Dispatch events that themes may listen for
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart }, bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:added', { detail: { cart }, bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart-update', { detail: { cart }, bubbles: true }));

    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate && typeof window.publish === 'function') {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'quick-view', cartData: cart });
    }

    // Use Shopify's Section Rendering API to refresh the header
    refreshHeaderSection();
  }

  /**
   * Find every cart-count element in the page header and update it.
   * Different themes use different class names — we cover the common ones.
   */
  function updateHeaderCartCount(count) {
    const selectors = [
      '[data-cart-count]',
      '.cart-count',
      '[class*="cart-count"]',
      '[class*="cart-bubble"]',
      '[class*="header__cart-count"]',
      'header [class*="count"]',
      'a[href="/cart"] [class*="count"]',
      'a[href="/cart"] [class*="bubble"]',
      'cart-icon [class*="count"]',
    ];

    // Find all matching elements, but skip the one inside our modal
    const elements = document.querySelectorAll(selectors.join(','));
    elements.forEach(el => {
      // Don't touch our own modal counter — we already updated it
      if (el.closest('.quick-view-modal')) return;
      el.textContent = count;

      // If it was hidden when count was 0, make it visible now
      if (count > 0) {
        el.removeAttribute('hidden');
        el.style.display = '';
      }
    });
  }

  /**
   * Ask Shopify to re-render just the header section with fresh cart data.
   * This is the "official" way to update sections after a cart change.
   */
  async function refreshHeaderSection() {
    try {
      const res = await fetch('/?sections=header,header-group', {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) return;
      const data = await res.json();

      // Replace each returned section in the DOM
      Object.keys(data).forEach(sectionName => {
        const newHtml = data[sectionName];
        if (!newHtml) return;

        // Find the section element by id
        const sectionEl = document.getElementById(`shopify-section-${sectionName}`) ||
                          document.querySelector(`[id*="${sectionName}"]`);
        if (sectionEl) {
          // Only update the cart count area to avoid full re-render flicker
          const parser = new DOMParser();
          const doc = parser.parseFromString(newHtml, 'text/html');
          const newCountEls = doc.querySelectorAll('[data-cart-count], [class*="cart-count"], [class*="cart-bubble"]');
          newCountEls.forEach(newEl => {
            // Find the matching element in our live DOM by structural position
            const matching = sectionEl.querySelector(`.${newEl.className.split(' ')[0]}`);
            if (matching) {
              matching.textContent = newEl.textContent;
              matching.innerHTML = newEl.innerHTML;
            }
          });
        }
      });
    } catch (e) {
      // non-fatal — we still have updateHeaderCartCount as fallback
    }
  }


  /**
   * Read the current cart on page load so the modal's counter starts correct.
   */
  async function syncCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      if (cartCountEl) {
        cartCountEl.textContent = cart.item_count || 0;
      }
      updateHeaderCartCount(cart.item_count || 0);
    } catch (e) {
      // non-fatal
    }
  }

  /**
   * Check if a specific variant has reached its max allowed quantity in the cart.
   *
   * Returns true ONLY when we have concrete proof the limit is hit:
   *   - The variant is currently in the cart, AND
   *   - We have a known inventory quantity, AND
   *   - Cart qty >= inventory qty
   *
   * When inventory data is missing/unknown, returns false (button stays enabled).
   * Shopify's own cart/add endpoint will reject if there's truly no stock, so this
   * is safe — we'd rather let the user click and get a real error than wrongly
   * disable buttons for items that have plenty of stock.
   */
  // async function isVariantAtMaxInCart(variantId, maxQty) {
  //   if (!variantId) return false;

  //   // Need a real number to compare against. If we don't know the limit,
  //   // don't disable — let the user try.
  //   if (maxQty === null || maxQty === undefined || isNaN(Number(maxQty))) {
  //     return false;
  //   }

  //   const max = Number(maxQty);
  //   if (max <= 0) return false; // 0 or negative means we don't really know

  //   try {
  //     const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
  //     if (!res.ok) return false;
  //     const cart = await res.json();
  //     const item = (cart.items || []).find(i => i.variant_id === Number(variantId));
  //     if (!item) return false;

  //     return item.quantity >= max;
  //   } catch (e) {
  //     return false;
  //   }
  // }
  /**
   * Decide whether the + button should be disabled for a variant.
   *
   * Returns true (disable +) when ANY of these is true:
   *   - The variant previously hit a 422 (max qty) error this session
   *   - The variant is in the cart AND cart qty >= known inventory qty
   *   - The variant is in the cart AND we have no inventory info
   *     (paintings are 1-of-1, so "in cart" = "maxed out")
   */
  async function isVariantAtMaxInCart(variantId, maxQty) {
    if (!variantId) return false;

    // Previously hit Shopify's max-qty error → always disabled
    if (maxedOutVariants.has(String(variantId))) return true;

    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return false;
      const cart = await res.json();
      const item = (cart.items || []).find(i => i.variant_id === Number(variantId));
      if (!item) return false; // not in cart → enabled

      // Item IS in cart. Decide based on known inventory.
      const max = Number(maxQty);
      if (!isNaN(max) && max > 0) {
        // We know the real limit — compare against it
        return item.quantity >= max;
      }

      // No reliable inventory number. For a 1-of-1 art store,
      // being in the cart means it's maxed out.
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Update the cart count in the modal with a bouncy animation.
   */
  function updateCartCount(newCount) {
    if (!cartCountEl) return;
    cartCountEl.textContent = newCount;
    cartCountEl.setAttribute('data-bumping', 'true');
    setTimeout(() => cartCountEl.removeAttribute('data-bumping'), 350);
  }

  /**
   * Find the cart icon in the header and trigger a brief animation
   * to draw attention to the newly added item.
   */
  function pulseCartIcon() {
    const cartIcon =
      document.querySelector('a[href="/cart"]') ||
      document.querySelector('[class*="cart-icon"]') ||
      document.querySelector('[class*="header__cart"]') ||
      document.querySelector('[ref="cartIcon"]') ||
      document.querySelector('[class*="cart-link"]');

    if (!cartIcon) return;

    cartIcon.classList.add('qv-cart-pulse');
    setTimeout(() => cartIcon.classList.remove('qv-cart-pulse'), 800);
  }

  function setupTouchGestures() {
    let touchStartY = 0;
    let touchStartX = 0;
    let touchStartTime = 0;
    let touchActive = false;
    let gestureDirection = null; // 'vertical' | 'horizontal' | null

    // modal.addEventListener('touchstart', (e) => {
    //   touchStartY = e.touches[0].clientY;
    //   touchStartX = e.touches[0].clientX;
    //   touchStartTime = Date.now();
    //   touchActive = true;
    //   gestureDirection = null;
    // }, { passive: true });

    // modal.addEventListener('touchmove', (e) => {
    //   if (!touchActive) return;

    //   const currentY = e.touches[0].clientY;
    //   const currentX = e.touches[0].clientX;
    //   const deltaY = currentY - touchStartY;
    //   const deltaX = currentX - touchStartX;
    //   const absY = Math.abs(deltaY);
    //   const absX = Math.abs(deltaX);

    //   // Lock the gesture direction once we've moved enough to know
    //   if (!gestureDirection && (absY > 20 || absX > 20)) {
    //     if (absX > absY * 1.2) {
    //       gestureDirection = 'horizontal';
    //     } else if (absY > absX * 1.2) {
    //       gestureDirection = 'vertical';
    //     }
    //   }

    //   // Prevent default for both directions so the page doesn't scroll
    //   if (gestureDirection) {
    //     e.preventDefault();
    //   }
    // }, { passive: false });

    // modal.addEventListener('touchend', (e) => {
    //   if (!touchActive) return;
    //   touchActive = false;

    //   const touchEndY = e.changedTouches[0].clientY;
    //   const touchEndX = e.changedTouches[0].clientX;
    //   const deltaY = touchEndY - touchStartY;
    //   const deltaX = touchEndX - touchStartX;
    //   const elapsed = Date.now() - touchStartTime;
    //   const absY = Math.abs(deltaY);
    //   const absX = Math.abs(deltaX);

    //   // Only register a swipe if it was a fast, intentional gesture
    //   if (elapsed >= 600) {
    //     gestureDirection = null;
    //     return;
    //   }

    //   // Vertical swipe → navigate between products
    //   if (gestureDirection === 'vertical' && absY > 60) {
    //     if (deltaY < 0) navigateToProduct(1);   // swipe up = next
    //     else navigateToProduct(-1);              // swipe down = previous
    //   }

    //   // Horizontal swipe → navigate carousel images within the same product
    //   if (gestureDirection === 'horizontal' && absX > 50) {
    //     if (deltaX < 0) {
    //       // Swipe left = next image
    //       const nextBtn = contentEl.querySelector('.qv-carousel__arrow--next');
    //       if (nextBtn) nextBtn.click();
    //     } else {
    //       // Swipe right = previous image
    //       const prevBtn = contentEl.querySelector('.qv-carousel__arrow--prev');
    //       if (prevBtn) prevBtn.click();
    //     }
    //   }

    //   gestureDirection = null;
    // }, { passive: true });

    // modal.addEventListener('touchcancel', () => {
    //   touchActive = false;
    //   gestureDirection = null;
    // }, { passive: true });
  }

  /* === Fullscreen image lightbox with zoom + pan === */

  // Zoom/pan state
  let lbScale = 1;
  let lbPanX = 0;
  let lbPanY = 0;
  const LB_MIN_SCALE = 1;
  const LB_MAX_SCALE = 4;

  function setupLightbox() {
    if (!lightbox || !lightboxImg) return;

    // Open: click a carousel image
    contentEl.addEventListener('click', (e) => {
      const img = e.target.closest('.qv-carousel__slide img');
      if (!img) return;
      if (e.target.closest('.qv-carousel__arrow, .qv-carousel__dot')) return;
      openLightbox(img.currentSrc || img.src);
    });

    // Close: X button
    if (lightboxClose) {
      lightboxClose.addEventListener('click', (e) => {
        e.stopPropagation();
        closeLightbox();
      });
    }

    // Escape closes
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lightbox.getAttribute('aria-hidden') === 'false') {
        e.stopPropagation();
        closeLightbox();
      }
    });

    setupLightboxZoom();
  }

  function openLightbox(src) {
    if (!lightbox || !lightboxImg || !src) return;
    lightboxImg.src = src;
    resetZoom();
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('qv-lightbox-open');
  }

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('qv-lightbox-open');
    resetZoom();
    setTimeout(() => {
      if (lightbox.getAttribute('aria-hidden') === 'true' && lightboxImg) {
        lightboxImg.src = '';
      }
    }, 300);
  }

  function applyZoomTransform() {
    lightboxImg.style.transform =
      `translate(${lbPanX}px, ${lbPanY}px) scale(${lbScale})`;
    lightboxImg.setAttribute('data-zoomed', lbScale > 1 ? 'true' : 'false');
  }

  function resetZoom() {
    lbScale = 1;
    lbPanX = 0;
    lbPanY = 0;
    if (lightboxImg) {
      lightboxImg.style.transform = '';
      lightboxImg.removeAttribute('data-zoomed');
      lightboxImg.removeAttribute('data-dragging');
    }
  }

  // Clamp pan so the image can't be dragged completely off-screen
  function clampPan() {
    const rect = lightboxImg.getBoundingClientRect();
    // How much the scaled image overflows the viewport, each side
    const overflowX = Math.max(0, (rect.width - window.innerWidth) / 2);
    const overflowY = Math.max(0, (rect.height - window.innerHeight) / 2);
    lbPanX = Math.max(-overflowX, Math.min(overflowX, lbPanX));
    lbPanY = Math.max(-overflowY, Math.min(overflowY, lbPanY));
  }

  function setupLightboxZoom() {
    const img = lightboxImg;

    /* ---- DESKTOP: scroll wheel to zoom ---- */
    lightbox.addEventListener('wheel', (e) => {
      if (lightbox.getAttribute('aria-hidden') !== 'false') return;
      e.preventDefault();
      const delta = -e.deltaY * 0.0015;
      const prevScale = lbScale;
      lbScale = Math.min(LB_MAX_SCALE, Math.max(LB_MIN_SCALE, lbScale + delta * lbScale));
      // If zoomed back to 1, recenter
      if (lbScale === 1) { lbPanX = 0; lbPanY = 0; }
      else clampPan();
      if (lbScale !== prevScale) applyZoomTransform();
    }, { passive: false });

    /* ---- DESKTOP: click image to toggle zoom; click background to close ---- */
    lightbox.addEventListener('click', (e) => {
      if (e.target.closest('.qv-lightbox__close')) return;

      // Click outside the image = close
      if (e.target !== img) {
        closeLightbox();
        return;
      }

      // Don't treat the end of a drag as a click
      if (img.dataset.justDragged === 'true') {
        img.dataset.justDragged = 'false';
        return;
      }

      // Click on the image = toggle between 1x and 2.5x
      if (lbScale > 1) {
        resetZoom();
      } else {
        lbScale = 2.5;
        applyZoomTransform();
      }
    });

    /* ---- DESKTOP: drag to pan when zoomed ---- */
    let dragging = false;
    let dragStartX = 0, dragStartY = 0, panStartX = 0, panStartY = 0;
    let moved = false;

    img.addEventListener('mousedown', (e) => {
      if (lbScale <= 1) return;
      dragging = true;
      moved = false;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      panStartX = lbPanX;
      panStartY = lbPanY;
      img.setAttribute('data-dragging', 'true');
      e.preventDefault();
    });

    window.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      lbPanX = panStartX + (e.clientX - dragStartX);
      lbPanY = panStartY + (e.clientY - dragStartY);
      if (Math.abs(e.clientX - dragStartX) > 3 || Math.abs(e.clientY - dragStartY) > 3) {
        moved = true;
      }
      clampPan();
      applyZoomTransform();
    });

    window.addEventListener('mouseup', () => {
      if (!dragging) return;
      dragging = false;
      img.removeAttribute('data-dragging');
      if (moved) img.dataset.justDragged = 'true';
    });

    /* ---- MOBILE: pinch to zoom + drag to pan ---- */
    let touchMode = null;       // 'pan' | 'pinch' | null
    let startDist = 0;
    let startScale = 1;
    let tPanStartX = 0, tPanStartY = 0;
    let tStartX = 0, tStartY = 0;
    let tMoved = false;
    let lastTapTime = 0;

    function distance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    }

    lightbox.addEventListener('touchstart', (e) => {
      if (lightbox.getAttribute('aria-hidden') !== 'false') return;
      tMoved = false;

      if (e.touches.length === 2) {
        touchMode = 'pinch';
        startDist = distance(e.touches);
        startScale = lbScale;
      } else if (e.touches.length === 1) {
        touchMode = 'pan';
        tStartX = e.touches[0].clientX;
        tStartY = e.touches[0].clientY;
        tPanStartX = lbPanX;
        tPanStartY = lbPanY;
      }
    }, { passive: true });

    lightbox.addEventListener('touchmove', (e) => {
      if (lightbox.getAttribute('aria-hidden') !== 'false') return;

      if (touchMode === 'pinch' && e.touches.length === 2) {
        e.preventDefault();
        const dist = distance(e.touches);
        lbScale = Math.min(LB_MAX_SCALE, Math.max(LB_MIN_SCALE, startScale * (dist / startDist)));
        if (lbScale === 1) { lbPanX = 0; lbPanY = 0; }
        else clampPan();
        applyZoomTransform();
        tMoved = true;
      } else if (touchMode === 'pan' && e.touches.length === 1 && lbScale > 1) {
        e.preventDefault();
        lbPanX = tPanStartX + (e.touches[0].clientX - tStartX);
        lbPanY = tPanStartY + (e.touches[0].clientY - tStartY);
        clampPan();
        applyZoomTransform();
        tMoved = true;
      }
    }, { passive: false });

    lightbox.addEventListener('touchend', (e) => {
      if (lightbox.getAttribute('aria-hidden') !== 'false') return;

      // Double-tap to toggle zoom
      if (!tMoved && e.changedTouches.length === 1) {
        const now = Date.now();
        if (now - lastTapTime < 300) {
          // double tap
          if (lbScale > 1) resetZoom();
          else { lbScale = 2.5; applyZoomTransform(); }
          lastTapTime = 0;
        } else {
          lastTapTime = now;
          // Single tap on background (not image) closes — check after delay
          const target = e.changedTouches[0];
          const el = document.elementFromPoint(target.clientX, target.clientY);
          if (el && !el.closest('.qv-lightbox__img') && lbScale === 1) {
            setTimeout(() => {
              if (Date.now() - lastTapTime >= 280) closeLightbox();
            }, 300);
          }
        }
      }
      touchMode = null;
    }, { passive: true });
  }

  function setupWheelGesture() {
    let wheelLocked = false;
    let wheelAccumulator = 0;
    let lastWheelTime = 0;

    modal.addEventListener('wheel', (e) => {
      if (modal.getAttribute('aria-hidden') !== 'false') return;
      if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return;

      const now = Date.now();
      if (now - lastWheelTime > 200) wheelAccumulator = 0;
      lastWheelTime = now;

      if (wheelLocked) {
        e.preventDefault();
        return;
      }

      wheelAccumulator += e.deltaY;
      const threshold = 80;

      if (wheelAccumulator > threshold) {
        e.preventDefault();
        wheelLocked = true;
        wheelAccumulator = 0;
        navigateToProduct(1);
        setTimeout(() => { wheelLocked = false; }, 600);
      } else if (wheelAccumulator < -threshold) {
        e.preventDefault();
        wheelLocked = true;
        wheelAccumulator = 0;
        navigateToProduct(-1);
        setTimeout(() => { wheelLocked = false; }, 600);
      } else {
        e.preventDefault();
      }
    }, { passive: false });
  }

  function closeModal() {
    if (!modal || modal.getAttribute('aria-hidden') === 'true') return;

    modal.setAttribute('aria-hidden', 'true');
    modal.removeAttribute('data-loaded');
    document.body.classList.remove('quick-view-open');

    // If URL is currently a product URL, restore it to homepage
    if (window.location.pathname.includes('/products/')) {
      history.replaceState({}, '', '/');
    } else if (history.state && history.state.quickView) {
      history.back();
    }

    if (previousFocus && previousFocus.focus) previousFocus.focus();

    setTimeout(() => {
      if (modal.getAttribute('aria-hidden') === 'true') {
        contentEl.innerHTML = '';
      }
    }, 350);
  }

  function handleKeydown(e) {
    if (modal.getAttribute('aria-hidden') !== 'false') return;
    if (lightbox && lightbox.getAttribute('aria-hidden') === 'false') return;

    if (e.key === 'Escape') closeModal();

    if (e.key === 'ArrowLeft') {
      const prev = contentEl.querySelector('.qv-carousel__arrow--prev');
      if (prev) prev.click();
    }
    if (e.key === 'ArrowRight') {
      const next = contentEl.querySelector('.qv-carousel__arrow--next');
      if (next) next.click();
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      navigateToProduct(-1);
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      navigateToProduct(1);
    }
  }

  function handlePopState() {
    if (modal.getAttribute('aria-hidden') === 'false') {
      modal.setAttribute('aria-hidden', 'true');
      modal.removeAttribute('data-loaded');
      document.body.classList.remove('quick-view-open');
      contentEl.innerHTML = '';
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();